import {
  HttpClient,
  HttpContext,
  HttpContextToken,
} from '@angular/common/http';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  NgZone,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import * as signalR from '@microsoft/signalr';
import { firstValueFrom, interval, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

export const SKIP_CUSTOM_HEADERS = new HttpContextToken<boolean>(() => false);

import { environment } from '@customer-portal/environments';
import { MaintenanceConfig } from '@customer-portal/shared/models';
import { AuthService } from '@customer-portal/shared/services';

import { LoggingService } from '../app-insights/logging.service';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggingService = inject(LoggingService);
  private readonly ngZone = inject(NgZone);
  private readonly maintenanceConfig = signal<MaintenanceConfig | null>(null);
  private readonly signalRConnectionState =
    signal<signalR.HubConnectionState | null>(null);
  private signalRConnection: signalR.HubConnection | null = null;
  private pollingSubscription: Subscription | null = null;
  private intentionalDisconnect = false;
  private reconnectExhausted = false;

  readonly maintenance$ = toObservable(this.maintenanceConfig);
  readonly isMaintenanceActive = computed(
    () => this.maintenanceConfig()?.active ?? false,
  );
  readonly isSignalRConnected = computed(
    () =>
      this.signalRConnectionState() === signalR.HubConnectionState.Connected,
  );

  constructor() {
    this.initializeLogoutHandler();
    this.initializeCleanup();
  }

  checkMaintenanceStatus(): Observable<MaintenanceConfig | null> {
    const cacheBuster = `&_t=${new Date().getTime()}`;
    const url = `${environment.maintenance.blobUrl}${cacheBuster}`;

    return this.http
      .get<MaintenanceConfig>(url, {
        context: new HttpContext().set(SKIP_CUSTOM_HEADERS, true),
        headers: { SKIP_LOADING: 'true' },
      })
      .pipe(
        tap((config) => {
          const current = this.maintenanceConfig();

          if (!this.isMaintenanceConfigEqual(current, config)) {
            this.ngZone.run(() => {
              this.maintenanceConfig.set(config);
            });
          }
        }),
        catchError((error: unknown) => {
          if (
            this.isNotFoundError(error) &&
            this.maintenanceConfig() !== null
          ) {
            this.ngZone.run(() => {
              this.maintenanceConfig.set(null);
            });
          }

          return of(null);
        }),
      );
  }

  async connectSignalR(): Promise<void> {
    this.stopPolling();
    this.intentionalDisconnect = false;
    this.reconnectExhausted = false;

    await firstValueFrom(this.checkMaintenanceStatus());

    try {
      this.signalRConnection = new signalR.HubConnectionBuilder()
        .withUrl(
          environment.maintenance.signalRNegotiateUrl,
          this.buildConnectionOptions(),
        )
        .withAutomaticReconnect(this.buildRetryStrategy())
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.registerSignalRHandlers();

      this.logConsole('About to start SignalR connection');
      await this.signalRConnection.start();
      this.updateConnectionState();
      this.log('SignalR connected successfully');
    } catch (error: unknown) {
      this.updateConnectionState();
      this.logError('SignalR connection error', error);
      this.startPolling();
    }
  }

  disconnect(): void {
    this.intentionalDisconnect = true;

    if (this.signalRConnection) {
      this.signalRConnection.stop();
      this.signalRConnection = null;
      this.updateConnectionState();
    }

    this.stopPolling();
  }

  private initializeLogoutHandler(): void {
    this.authService.logout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.disconnect();
      });
  }

  private initializeCleanup(): void {
    this.destroyRef.onDestroy(() => {
      this.disconnect();
    });
  }

  private buildConnectionOptions(): signalR.IHttpConnectionOptions {
    const options: signalR.IHttpConnectionOptions = {
      headers: {
        'Ocp-Apim-Subscription-Key':
          environment.maintenance.apimSubscriptionMaintenanceKey,
      },
      withCredentials: true,
    };

    return options;
  }

  private buildRetryStrategy(): signalR.IRetryPolicy {
    const MAX_RETRY_ATTEMPTS = 5;

    return {
      nextRetryDelayInMilliseconds: (retryContext: {
        previousRetryCount: number;
      }) => {
        if (retryContext.previousRetryCount >= MAX_RETRY_ATTEMPTS) {
          this.loggingService.logTrace(
            `SignalR max retry attempts (${MAX_RETRY_ATTEMPTS}) reached after ${retryContext.previousRetryCount} retries, stopping reconnect`,
          );
          this.reconnectExhausted = true;

          return null;
        }

        const delayMs = Math.min(
          1000 * 2 ** retryContext.previousRetryCount,
          30000,
        );
        this.loggingService.logTrace(
          `SignalR retry attempt ${retryContext.previousRetryCount + 1}/${MAX_RETRY_ATTEMPTS}, waiting ${delayMs}ms`,
        );

        return delayMs;
      },
    };
  }

  private registerSignalRHandlers(): void {
    if (!this.signalRConnection) return;

    this.signalRConnection.on(
      'maintenanceUpdate',
      (data: MaintenanceConfig | null) => {
        this.loggingService.logTrace('SignalR maintenanceUpdate received', {
          data,
        });

        let normalizedData: MaintenanceConfig | null = data;

        if (data && typeof data === 'object' && 'active' in data) {
          normalizedData = data.active ? data : null;
        }

        const current = this.maintenanceConfig();

        if (!this.isMaintenanceConfigEqual(current, normalizedData)) {
          this.ngZone.run(() => {
            this.maintenanceConfig.set(normalizedData);
          });
        }
      },
    );

    this.signalRConnection.onclose((error: Error | undefined) => {
      this.updateConnectionState();
      this.log('SignalR connection closed', {
        error,
        reconnectExhausted: this.reconnectExhausted,
        intentionalDisconnect: this.intentionalDisconnect,
      });

      if (!this.intentionalDisconnect && !this.reconnectExhausted) {
        this.startPolling();
      } else if (this.reconnectExhausted) {
        this.log('Reconnect attempts exhausted, starting polling only');
        this.startPolling();
      } else {
        this.logConsole('Intentional disconnect, skipping polling');
      }
    });

    this.signalRConnection.onreconnecting(() => {
      this.updateConnectionState();
      this.log('SignalR reconnecting');
    });

    this.signalRConnection.onreconnected(() => {
      this.updateConnectionState();
      this.log('SignalR reconnected');
      this.stopPolling();
      this.reconnectExhausted = false;
    });
  }

  private async startPolling(): Promise<void> {
    if (this.pollingSubscription) {
      this.logConsole('Polling already active, skipping');

      return;
    }
    this.logConsole('Starting fallback polling (60s interval)');

    try {
      await firstValueFrom(this.checkMaintenanceStatus());
    } catch (error: unknown) {
      this.log('Initial maintenance check failed, continuing with polling', {
        error,
      });
    }

    this.logConsole('Polling subscription created');

    this.ngZone.runOutsideAngular(() => {
      this.pollingSubscription = interval(60000)
        .pipe(switchMap(() => this.checkMaintenanceStatus()))
        .subscribe();
    });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private updateConnectionState(): void {
    this.ngZone.run(() => {
      this.signalRConnectionState.set(this.signalRConnection?.state ?? null);
    });
  }

  private log(message: string, data?: { [key: string]: unknown }): void {
    this.loggingService.logToConsole(`[MaintenanceService] ${message}`, data);
    this.loggingService.logTrace(message, data);
  }

  private logConsole(message: string, data?: { [key: string]: unknown }): void {
    this.loggingService.logToConsole(`[MaintenanceService] ${message}`, data);
  }

  private logError(message: string, error: unknown): void {
    this.loggingService.logToConsole(`[MaintenanceService] ${message}`, {
      error,
    });
    this.loggingService.logException(
      error instanceof Error ? error : new Error(message),
    );
  }

  private isNotFoundError(error: unknown): boolean {
    return (
      error !== null &&
      typeof error === 'object' &&
      'status' in error &&
      error.status === 404
    );
  }

  private isMaintenanceConfigEqual(
    a: MaintenanceConfig | null,
    b: MaintenanceConfig | null,
  ): boolean {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;

    return (
      a.active === b.active &&
      a.startTime === b.startTime &&
      a.endTime === b.endTime &&
      a.message === b.message
    );
  }
}
